import { create } from 'zustand';
import { startOfWeek, addDays, addMonths, startOfMonth, format, parseDateKey, parseTimeToHour } from './dateUtils';
import { DEFAULT_WORK_HOURS, hoursForDate, legacyToWorkHours, openWeekdays } from '../../../utils/weekdayHours';

// The API's work_schedule: days/weekdays are the per-weekday hours, the four old keys their
// bounds (first/last open day, earliest start, latest end), which the time grid uses as its rows.
const DEFAULT_WORK_SCHEDULE = {
  weekStart: 0,  // Monday
  weekEnd: 4,    // Friday
  dayStart: '08:00',
  dayEnd: '16:00',
  days: DEFAULT_WORK_HOURS,
  weekdays: [0, 1, 2, 3, 4],
};

const sameWorkSchedule = (previous, next) =>
  JSON.stringify(previous) === JSON.stringify(next) ? previous : next;

export const workScheduleFromApi = (schedule) => {
  if (!schedule) return DEFAULT_WORK_SCHEDULE;
  // A response from before per-weekday hours has only the range and one period
  const days = schedule.days
    || legacyToWorkHours(schedule.week_start, schedule.week_end, schedule.day_start, schedule.day_end);
  return {
    weekStart: schedule.week_start,
    weekEnd: schedule.week_end,
    dayStart: schedule.day_start,
    dayEnd: schedule.day_end,
    days,
    weekdays: schedule.weekdays || openWeekdays(days),
  };
};

// Storage key prefix for calendar preferences (org-specific)
const CALENDAR_PREFS_KEY_PREFIX = 'calendar_preferences_';

// Default preferences
const defaultPreferences = {
  currentView: 'employee',
  calendarMode: 'week', // 'week' | 'day' | 'month'
  filterPanelOpen: false,
  sidebarCollapsed: false,
  navHidden: false, // hide the app navigation drawer to widen the calendar (opt-in, per browser)
  legendHidden: null, // null = follow screen size default, true/false = user manually toggled
  // Month view: "does an N h job fit" threshold of the open days (null = the server's default,
  // 16 h). Remembered per browser: a shop that plans 8 h jobs asks the same question daily.
  monthOpenHours: null,
  filters: {
    employees: [],
    serviceBays: [],
    states: [],
    colorTags: [],
    // Visibility overrides (null = use org defaults)
    visibleWeekdays: null, // Array of day indices (0=Mon, 6=Sun) or null for org default
    visibleStartHour: null, // Number (5-22) or null for org default
    visibleEndHour: null, // Number (5-22) or null for org default
  },
};

// Get organization-specific storage key
const getStorageKey = () => {
  const orgId = localStorage.getItem('currentOrganizationId');
  return orgId ? `${CALENDAR_PREFS_KEY_PREFIX}${orgId}` : null;
};

// Load preferences from localStorage
const loadPreferences = () => {
  try {
    const storageKey = getStorageKey();
    if (!storageKey) return defaultPreferences;

    const saved = localStorage.getItem(storageKey);
    if (saved) {
      const parsed = JSON.parse(saved);
      // Merge with defaults to handle any new fields
      return {
        ...defaultPreferences,
        ...parsed,
        filters: {
          ...defaultPreferences.filters,
          ...parsed.filters,
        },
      };
    }
  } catch (e) {
    console.error('Failed to load calendar preferences:', e);
  }
  return defaultPreferences;
};

// Save preferences to localStorage
const savePreferences = (prefs) => {
  try {
    const storageKey = getStorageKey();
    if (!storageKey) return;

    localStorage.setItem(storageKey, JSON.stringify(prefs));
  } catch (e) {
    console.error('Failed to save calendar preferences:', e);
  }
};

const useCalendarState = create((set, get) => ({
  // View state
  currentView: 'employee', // 'employee' or 'bay'
  calendarMode: 'week', // 'week' | 'day' | 'month'
  currentDate: new Date(),
  selectedDay: null, // Date object for day mode (null = use currentDate)

  // Month mode: the aggregate payload of /api/scheduling/calendar/month/ and the day whose
  // peek panel is open ('YYYY-MM-DD'). monthVersion is bumped by every local event, absence
  // and note change so the month aggregates refetch instead of going stale.
  monthData: null,
  selectedMonthDay: null,
  monthVersion: 0,
  monthOpenHours: null,

  // Data
  employees: [],
  serviceBays: [],
  events: [],
  unavailabilities: [],
  calendarNotes: [],
  unscheduledMaintenances: [],
  unscheduledTotalCount: 0,
  unscheduledHasMore: false,
  unscheduledLoadingMore: false,
  bookingEvents: [],
  breakPeriodEvents: [],
  colorTags: [],   // every tag of the org, inactive included (old cards still resolve a colour)

  // Organization settings
  timezone: 'Europe/Helsinki',
  workSchedule: DEFAULT_WORK_SCHEDULE,

  // UI state
  loading: false,
  error: null,
  sidebarCollapsed: false,
  filterPanelOpen: false,
  // Seeded from storage rather than defaulted: Dashboard reads this on first paint, and a
  // drawer that flashes in and then disappears on every calendar load looks broken.
  navHidden: loadPreferences().navHidden,
  legendHidden: null, // null = follow screen size default, true/false = user override

  // Filters
  filters: {
    employees: [],      // Array of employee IDs to show (empty = all)
    serviceBays: [],    // Array of service bay IDs to show (empty = all)
    states: [],         // Array of maintenance states to show (empty = all)
    colorTags: [],      // Array of colour tag IDs, FILTER_NONE for untagged (empty = all)
    // Visibility overrides (null = use org defaults)
    visibleWeekdays: null, // Array of day indices (0=Mon, 6=Sun) or null for org default
    visibleStartHour: null, // Number (5-22) or null for org default
    visibleEndHour: null, // Number (5-22) or null for org default
  },

  // Event drag state (for moving/resizing existing events)
  eventDragState: null, // { type: 'move'|'resize-top'|'resize-bottom', eventType: 'maintenance'|'unavailability', event, originalEvent, startY }

  // Drop target state (for visual feedback)
  dropTarget: null, // { resourceId, dayIndex, hour }

  // Sidebar drag state (for dragging unscheduled maintenances to calendar)
  draggedMaintenance: null, // Maintenance being dragged from sidebar

  // Dialog state
  scheduleDialogOpen: false,
  scheduleDialogMode: 'existing', // 'new' or 'existing' - whether to create new maintenance or link existing
  unavailabilityDialogOpen: false,
  noteDialogOpen: false,
  contextMenuOpen: false,
  contextMenuPosition: { x: 0, y: 0 },
  selectedMaintenance: null,
  selectedTimeSlot: null,
  editingEvent: null, // For editing existing scheduled events
  editingUnavailability: null, // For editing existing unavailabilities
  editingNote: null, // For editing existing calendar notes
  eventContextMenu: null, // { event, position: { x, y } } — right-click menu on a maintenance card

  // Drag overlay callback (for clearing the visual selection overlay)
  dragOverlayHideCallback: null,

  // Highlight state (for navigation from maintenance detail)
  highlightedMaintenanceId: null,
  highlightedScheduleId: null, // Narrows the highlight to one slot when a work order has several
  highlightedUnscheduledId: null, // For highlighting unscheduled maintenance in sidebar

  // Calendar search ("Hae kalenterista") state
  searchQuery: '',        // The query the current results belong to
  searchResults: [],      // Matches from /api/scheduling/calendar/search/
  searchIndex: -1,        // Which match we are currently showing
  searchLoading: false,
  searchError: null,      // Translated, user-safe message (never a raw exception)
  searchNotFound: false,

  // Scheduling mode (activated from maintenance detail "Aikatauluta kalenterissa")
  schedulingModeTarget: null, // { id, work_number, vehicle_registration, customer_name, description }

  // Computed values
  getWeekDates: () => {
    const { currentDate, workSchedule, filters } = get();
    const monday = startOfWeek(currentDate);

    // Use filter override if set, otherwise use org defaults
    if (filters.visibleWeekdays && filters.visibleWeekdays.length > 0) {
      // Sort weekdays and return dates for each
      const sortedDays = [...filters.visibleWeekdays].sort((a, b) => a - b);
      return sortedDays.map((dayIndex) => addDays(monday, dayIndex));
    }

    // Default: the organization's open weekdays (a closed day is not a column)
    return workSchedule.weekdays.map((dayIndex) => addDays(monday, dayIndex));
  },

  // Get effective work hours (filter override or org default)
  // Returns time strings like "08:00"
  getEffectiveWorkHours: () => {
    const { workSchedule, filters } = get();
    return {
      dayStart: filters.visibleStartHour ?? workSchedule.dayStart ?? '08:00',
      dayEnd: filters.visibleEndHour ?? workSchedule.dayEnd ?? '16:00',
    };
  },

  // The organization's work hours of one date as {dayStart, dayEnd} ("HH:MM"), or null when the
  // workshop is closed that weekday. The grid rows are the union of all open days
  // (getEffectiveWorkHours); this is what a single day's defaults and shading use.
  getHoursForDate: (date) => {
    const hours = hoursForDate(get().workSchedule.days, date);
    return hours ? { dayStart: hours.start, dayEnd: hours.end } : null;
  },

  // 'YYYY-MM' of the month the month view shows
  getMonthKey: () => format(get().currentDate, 'yyyy-MM'),

  getWeekRange: () => {
    const { calendarMode, selectedDay, currentDate } = get();
    if (calendarMode === 'day') {
      const day = selectedDay || currentDate;
      const dayStr = format(day, 'yyyy-MM-dd');
      return { start: dayStr, end: dayStr };
    }
    const dates = get().getWeekDates();
    return {
      start: format(dates[0], 'yyyy-MM-dd'),
      end: format(dates[dates.length - 1], 'yyyy-MM-dd'),
    };
  },

  // Helper to get current preferences for saving
  _getPrefsForSave: () => {
    const { currentView, calendarMode, filterPanelOpen, sidebarCollapsed, navHidden, legendHidden, monthOpenHours, filters } = get();
    return { currentView, calendarMode, filterPanelOpen, sidebarCollapsed, navHidden, legendHidden, monthOpenHours, filters };
  },

  // Actions
  setCurrentView: (view) => {
    set({ currentView: view });
    savePreferences({ ...get()._getPrefsForSave(), currentView: view });
  },

  setCalendarMode: (mode) => {
    const { calendarMode, selectedDay, selectedMonthDay, currentDate } = get();
    const updates = { calendarMode: mode };
    // Leaving month mode lands on the peeked day, or on the month being viewed; leaving day
    // mode lands on the selected day; leaving week mode for the month follows the viewed
    // week, not a day selected long ago (week to day keeps its old habit).
    let anchor;
    if (calendarMode === 'month') {
      anchor = selectedMonthDay ? parseDateKey(selectedMonthDay) : currentDate;
    } else if (calendarMode === 'day') {
      anchor = selectedDay || currentDate;
    } else {
      anchor = mode === 'month' ? currentDate : selectedDay;
    }
    if (mode === 'day') {
      if (anchor) {
        updates.selectedDay = anchor;
      } else if (!selectedDay) {
        updates.selectedDay = currentDate;
      }
    } else if (anchor) {
      // Navigate the week / month to the period containing the anchor day
      updates.currentDate = anchor;
    }
    if (mode !== 'month') {
      updates.selectedMonthDay = null;
    }
    set(updates);
    savePreferences({ ...get()._getPrefsForSave(), calendarMode: mode });
  },

  setMonthData: (data) => set({ monthData: data }),

  // null restores the server's default rule; anything else is hours (> 0).
  setMonthOpenHours: (hours) => {
    const value = hours === null || hours === undefined || !(hours > 0) ? null : hours;
    set({ monthOpenHours: value });
    savePreferences({ ...get()._getPrefsForSave(), monthOpenHours: value });
  },

  setSelectedMonthDay: (dateKey) => set({ selectedMonthDay: dateKey }),

  goToNextMonth: () => {
    const { currentDate } = get();
    set({ currentDate: addMonths(startOfMonth(currentDate), 1), selectedMonthDay: null });
  },

  goToPrevMonth: () => {
    const { currentDate } = get();
    set({ currentDate: addMonths(startOfMonth(currentDate), -1), selectedMonthDay: null });
  },

  // The month view has no time axis, so its create action hands ScheduleDialog the first two
  // hours of the clicked date's work day through selectedTimeSlot (the slot shape
  // DragSelection sets); the dialog asks for the exact time.
  openScheduleDialogForDay: (dateKey, maintenance = null) => {
    const { workSchedule } = get();
    const date = parseDateKey(dateKey);
    // That weekday's hours; a closed day (someone works a Saturday) falls back to the week's bounds
    const hours = get().getHoursForDate(date) || workSchedule;
    const dayStart = parseTimeToHour(hours.dayStart, 8);
    const dayEnd = parseTimeToHour(hours.dayEnd, 16);
    set({
      selectedTimeSlot: {
        date,
        startHour: dayStart,
        endHour: Math.min(dayStart + 2, dayEnd),
      },
    });
    get().openScheduleDialog(maintenance, maintenance ? 'existing' : 'new');
  },


  switchToDay: (date) => {
    set({ calendarMode: 'day', selectedDay: date });
    savePreferences({ ...get()._getPrefsForSave(), calendarMode: 'day' });
  },

  setLegendHidden: (value) => {
    set({ legendHidden: value });
    savePreferences({ ...get()._getPrefsForSave(), legendHidden: value });
  },

  // Initialize preferences from localStorage (call when calendar mounts)
  initializePreferences: () => {
    const prefs = loadPreferences();
    set({
      currentView: prefs.currentView,
      calendarMode: prefs.calendarMode || 'week',
      filterPanelOpen: prefs.filterPanelOpen,
      sidebarCollapsed: prefs.sidebarCollapsed,
      navHidden: prefs.navHidden,
      legendHidden: prefs.legendHidden,
      monthOpenHours: prefs.monthOpenHours > 0 ? prefs.monthOpenHours : null,
      filters: prefs.filters,
    });
  },

  setCurrentDate: (date) => set({ currentDate: date }),

  goToNextWeek: () => {
    const { currentDate } = get();
    set({ currentDate: addDays(currentDate, 7) });
  },

  goToPrevWeek: () => {
    const { currentDate } = get();
    set({ currentDate: addDays(currentDate, -7) });
  },

  goToToday: () => {
    const { calendarMode } = get();
    if (calendarMode === 'day') {
      set({ selectedDay: new Date() });
    } else if (calendarMode === 'month') {
      const today = new Date();
      set({ currentDate: today, selectedMonthDay: format(today, 'yyyy-MM-dd') });
    } else {
      set({ currentDate: new Date() });
    }
  },

  goToNextDay: () => {
    const { selectedDay, currentDate } = get();
    const day = selectedDay || currentDate;
    set({ selectedDay: addDays(day, 1) });
  },

  goToPrevDay: () => {
    const { selectedDay, currentDate } = get();
    const day = selectedDay || currentDate;
    set({ selectedDay: addDays(day, -1) });
  },

  // Navigate to a specific date (from URL param)
  setCurrentDateFromString: (dateStr) => {
    if (dateStr) {
      // A plain YYYY-MM-DD is a local date; new Date() would read it as UTC midnight and
      // land a day early west of Greenwich.
      const date = /^\d{4}-\d{2}-\d{2}$/.test(dateStr) ? parseDateKey(dateStr) : new Date(dateStr);
      if (date && !isNaN(date.getTime())) {
        // Day mode shows selectedDay, which outlives a visit to another page: without it a
        // calendar left in day mode stayed on its old day and the linked card was not there.
        set({ currentDate: date, selectedDay: date });
      }
    }
  },

  // Highlight a specific maintenance (from URL param)
  setHighlightedMaintenance: (maintenanceId) => set({ highlightedMaintenanceId: maintenanceId }),

  // Clear highlight
  clearHighlightedMaintenance: () => set({ highlightedMaintenanceId: null, highlightedScheduleId: null }),

  // --- Calendar search ---------------------------------------------------
  setSearchLoading: (loading) => set({ searchLoading: loading, searchError: null }),

  setSearchError: (message) => set({
    searchError: message,
    searchLoading: false,
    searchResults: [],
    searchIndex: -1,
    searchNotFound: false,
  }),

  setSearchResults: (query, results) => set({
    searchQuery: query,
    searchResults: results,
    searchIndex: -1,
    searchLoading: false,
    searchError: null,
    searchNotFound: results.length === 0,
  }),

  // The search box's own state, without the highlights (those may come from a link instead)
  resetSearchFields: () => set({
    searchQuery: '',
    searchResults: [],
    searchIndex: -1,
    searchLoading: false,
    searchError: null,
    searchNotFound: false,
  }),

  clearSearch: () => set({
    searchQuery: '',
    searchResults: [],
    searchIndex: -1,
    searchLoading: false,
    searchError: null,
    searchNotFound: false,
    highlightedMaintenanceId: null,
    highlightedScheduleId: null,
    highlightedUnscheduledId: null,
  }),

  // Navigate the calendar to one search result and highlight it. Changing currentDate /
  // selectedDay is what retriggers the calendar fetch (see MainCalendarView's effect).
  applySearchResult: (index) => {
    const { searchResults, calendarMode } = get();
    const result = searchResults[index];
    if (!result) return;

    if (!result.is_scheduled) {
      // No calendar slot: the work order lives in the unscheduled sidebar, which
      // highlights and scrolls itself. Expand the sidebar if it is collapsed.
      if (get().sidebarCollapsed) {
        get().toggleSidebar();
      }
      set({
        searchIndex: index,
        highlightedUnscheduledId: result.maintenance_id,
        highlightedMaintenanceId: null,
        highlightedScheduleId: null,
      });
      return;
    }

    // local_start_date is the org-timezone date computed by the backend, so parse it as a
    // plain local date rather than going through the browser's timezone.
    const [year, month, day] = (result.local_start_date || '').split('-').map(Number);
    const targetDate = year ? new Date(year, month - 1, day) : null;

    set({
      searchIndex: index,
      highlightedMaintenanceId: result.maintenance_id,
      highlightedScheduleId: result.schedule_id,
      highlightedUnscheduledId: null,
      ...(targetDate
        ? calendarMode === 'day'
          ? { selectedDay: targetDate }
          : calendarMode === 'month'
            ? { currentDate: targetDate, selectedMonthDay: result.local_start_date }
            : { currentDate: targetDate }
        : {}),
    });
  },

  // Highlight an unscheduled maintenance in sidebar (from URL param)
  setHighlightedUnscheduled: (maintenanceId) => set({ highlightedUnscheduledId: maintenanceId }),

  // Clear unscheduled highlight
  clearHighlightedUnscheduled: () => set({ highlightedUnscheduledId: null }),

  // Scheduling mode actions
  enterSchedulingMode: (maintenance) => set({ schedulingModeTarget: maintenance }),
  exitSchedulingMode: () => set({ schedulingModeTarget: null }),

  toggleSidebar: () => {
    set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed }));
    savePreferences(get()._getPrefsForSave());
  },

  // Hides the app navigation drawer while the calendar is open, giving the grid its 260px.
  // Opt-in and per browser; Dashboard only honours it on the calendar route.
  toggleNavHidden: () => {
    set((state) => ({ navHidden: !state.navHidden }));
    savePreferences(get()._getPrefsForSave());
  },

  toggleFilterPanel: () => {
    set((state) => ({ filterPanelOpen: !state.filterPanelOpen }));
    savePreferences(get()._getPrefsForSave());
  },

  setFilters: (filters) => {
    set({ filters });
    savePreferences(get()._getPrefsForSave());
  },

  updateFilters: (updates) => {
    set((state) => ({
      filters: { ...state.filters, ...updates },
    }));
    savePreferences(get()._getPrefsForSave());
  },

  resetFilters: () => {
    const defaultFilters = {
      employees: [],
      serviceBays: [],
      states: [],
      colorTags: [],
      visibleWeekdays: null,
      visibleStartHour: null,
      visibleEndHour: null,
    };
    set({ filters: defaultFilters });
    savePreferences({ ...get()._getPrefsForSave(), filters: defaultFilters });
  },

  // Get count of active filters
  getActiveFilterCount: () => {
    const { filters } = get();
    let count = 0;
    if (filters.employees.length > 0) count++;
    if (filters.serviceBays.length > 0) count++;
    if (filters.states.length > 0) count++;
    if ((filters.colorTags || []).length > 0) count++;
    if (filters.visibleWeekdays !== null) count++;
    if (filters.visibleStartHour !== null || filters.visibleEndHour !== null) count++;
    return count;
  },

  setLoading: (loading) => set({ loading }),

  setError: (error) => set({ error }),

  setCalendarData: (data) => set((state) => ({
    employees: data.employees || [],
    serviceBays: data.service_bays || [],
    events: data.events || [],
    unavailabilities: data.unavailabilities || [],
    calendarNotes: data.calendar_notes || [],
    bookingEvents: data.booking_events || [],
    breakPeriodEvents: data.break_period_events || [],
    colorTags: data.color_tags || [],
    timezone: data.timezone || 'Europe/Helsinki',
    // The same object while the hours do not change, so memos and effects that depend on it
    // (grid rows, an open note dialog's form) do not rerun on every calendar fetch
    workSchedule: sameWorkSchedule(state.workSchedule, workScheduleFromApi(data.work_schedule)),
  })),

  setUnscheduledMaintenances: (maintenances) => set({ unscheduledMaintenances: maintenances }),

  setUnscheduledData: (results, totalCount, hasMore) => set({
    unscheduledMaintenances: results,
    unscheduledTotalCount: totalCount,
    unscheduledHasMore: hasMore,
  }),

  appendUnscheduledMaintenances: (results, hasMore) => set((state) => {
    const existingIds = new Set(state.unscheduledMaintenances.map((m) => m.id));
    const newItems = results.filter((m) => !existingIds.has(m.id));
    return {
      unscheduledMaintenances: [...state.unscheduledMaintenances, ...newItems],
      unscheduledHasMore: hasMore,
    };
  }),

  setUnscheduledLoadingMore: (loading) => set({ unscheduledLoadingMore: loading }),

  // Patch one unscheduled row in place (e.g. after its colour tag changed), so the sidebar
  // repaints without refetching the list.
  updateUnscheduledMaintenance: (maintenanceId, updates) => set((state) => ({
    unscheduledMaintenances: state.unscheduledMaintenances.map((m) =>
      m.id === maintenanceId ? { ...m, ...updates } : m
    ),
  })),

  // Dialog actions
  openScheduleDialog: (maintenance = null, mode = 'existing') => set({
    scheduleDialogOpen: true,
    scheduleDialogMode: mode,
    selectedMaintenance: maintenance,
    editingEvent: null,
    contextMenuOpen: false,
    eventContextMenu: null,
  }),

  openEditEventDialog: (event) => set({
    scheduleDialogOpen: true,
    editingEvent: event,
    selectedMaintenance: null,
    selectedTimeSlot: null,
    contextMenuOpen: false,
    eventContextMenu: null,
  }),

  closeScheduleDialog: () => {
    const { dragOverlayHideCallback } = get();
    if (dragOverlayHideCallback) {
      dragOverlayHideCallback();
    }
    set({
      scheduleDialogOpen: false,
      scheduleDialogMode: 'existing',
      selectedMaintenance: null,
      selectedTimeSlot: null,
      editingEvent: null,
    });
  },

  openUnavailabilityDialog: () => set({
    unavailabilityDialogOpen: true,
    editingUnavailability: null,
    contextMenuOpen: false,
    eventContextMenu: null,
  }),

  // The phone has no drag selection: the tapped hour in a mechanic's column (the mobile
  // dialog's slot shape) is handed over as the absence's span and employee.
  openUnavailabilityDialogForSlot: (slot) => set({
    selectedTimeSlot: slot,
    unavailabilityDialogOpen: true,
    editingUnavailability: null,
    contextMenuOpen: false,
    eventContextMenu: null,
  }),

  openEditUnavailabilityDialog: (unavailability) => set({
    unavailabilityDialogOpen: true,
    editingUnavailability: unavailability,
    selectedTimeSlot: null,
    contextMenuOpen: false,
    eventContextMenu: null,
  }),

  closeUnavailabilityDialog: () => {
    const { dragOverlayHideCallback } = get();
    if (dragOverlayHideCallback) {
      dragOverlayHideCallback();
    }
    set({
      unavailabilityDialogOpen: false,
      editingUnavailability: null,
      selectedTimeSlot: null,
    });
  },

  openNoteDialog: () => set({
    noteDialogOpen: true,
    editingNote: null,
    contextMenuOpen: false,
    eventContextMenu: null,
  }),

  // The phone has no drag selection: the tapped hour slot (the mobile dialog's slot shape,
  // the same fields DragSelection sets) is handed over as the note's span.
  openNoteDialogForSlot: (slot) => set({
    selectedTimeSlot: slot,
    noteDialogOpen: true,
    editingNote: null,
    contextMenuOpen: false,
    eventContextMenu: null,
  }),

  openEditNoteDialog: (note) => set({
    noteDialogOpen: true,
    editingNote: note,
    selectedTimeSlot: null,
    contextMenuOpen: false,
    eventContextMenu: null,
  }),

  closeNoteDialog: () => {
    const { dragOverlayHideCallback } = get();
    if (dragOverlayHideCallback) {
      dragOverlayHideCallback();
    }
    set({
      noteDialogOpen: false,
      editingNote: null,
      selectedTimeSlot: null,
    });
  },

  closeContextMenu: () => set({
    contextMenuOpen: false,
    selectedTimeSlot: null,
  }),

  // Right-click menu on a maintenance card. Mutually exclusive with the drag-selection menu.
  openEventContextMenu: (event, position) => {
    const { dragOverlayHideCallback } = get();
    if (dragOverlayHideCallback) {
      dragOverlayHideCallback();
    }
    set({
      eventContextMenu: { event, position },
      contextMenuOpen: false,
      selectedTimeSlot: null,
    });
  },

  closeEventContextMenu: () => set({ eventContextMenu: null }),

  // Drag overlay management
  setDragOverlayHideCallback: (callback) => set({ dragOverlayHideCallback: callback }),

  clearDragSelection: () => {
    const { dragOverlayHideCallback } = get();
    if (dragOverlayHideCallback) {
      dragOverlayHideCallback();
    }
    set({ selectedTimeSlot: null });
  },

  // Event actions
  addEvent: (event) => set((state) => ({
    events: [...state.events, event],
    monthVersion: state.monthVersion + 1,
  })),

  updateEvent: (eventId, updates) => set((state) => ({
    events: state.events.map((e) =>
      e.id === eventId ? { ...e, ...updates } : e
    ),
    monthVersion: state.monthVersion + 1,
  })),

  updateEmployee: (employeeId, updates) => set((state) => ({
    employees: state.employees.map((e) =>
      e.id === employeeId ? { ...e, ...updates } : e
    ),
  })),

  removeEvent: (eventId) => set((state) => ({
    events: state.events.filter((e) => e.id !== eventId),
    monthVersion: state.monthVersion + 1,
  })),

  // A work order can own several schedule cards; state lives on the work order, so a state
  // change must repaint (or remove) every card sharing the maintenance_id.
  updateEventsByMaintenance: (maintenanceId, updates) => set((state) => ({
    events: state.events.map((e) =>
      e.maintenance_id === maintenanceId ? { ...e, ...updates } : e
    ),
    monthVersion: state.monthVersion + 1,
  })),

  removeEventsByMaintenance: (maintenanceId) => set((state) => ({
    events: state.events.filter((e) => e.maintenance_id !== maintenanceId),
    monthVersion: state.monthVersion + 1,
  })),

  removeFromUnscheduled: (maintenanceId) => set((state) => ({
    unscheduledMaintenances: state.unscheduledMaintenances.filter(
      (m) => m.id !== maintenanceId
    ),
    unscheduledTotalCount: Math.max(0, state.unscheduledTotalCount - 1),
  })),

  addToUnscheduled: (maintenance) => set((state) => ({
    unscheduledMaintenances: [...state.unscheduledMaintenances, maintenance],
    unscheduledTotalCount: state.unscheduledTotalCount + 1,
  })),

  // Unavailability actions
  addUnavailability: (unavailability) => set((state) => ({
    unavailabilities: [...state.unavailabilities, unavailability],
    monthVersion: state.monthVersion + 1,
  })),

  updateUnavailability: (unavailabilityId, updates) => set((state) => ({
    unavailabilities: state.unavailabilities.map((u) =>
      u.id === unavailabilityId ? { ...u, ...updates } : u
    ),
    monthVersion: state.monthVersion + 1,
  })),

  removeUnavailability: (unavailabilityId) => set((state) => ({
    unavailabilities: state.unavailabilities.filter((u) => u.id !== unavailabilityId),
    monthVersion: state.monthVersion + 1,
  })),

  // Calendar note actions
  addCalendarNote: (note) => set((state) => ({
    calendarNotes: [...state.calendarNotes, note],
    monthVersion: state.monthVersion + 1,
  })),

  updateCalendarNote: (noteId, updates) => set((state) => ({
    calendarNotes: state.calendarNotes.map((n) =>
      n.id === noteId ? { ...n, ...updates } : n
    ),
    monthVersion: state.monthVersion + 1,
  })),

  removeCalendarNote: (noteId) => set((state) => ({
    calendarNotes: state.calendarNotes.filter((n) => n.id !== noteId),
    monthVersion: state.monthVersion + 1,
  })),

  // Event drag actions (for moving/resizing existing events)
  startEventDrag: (dragState) => set({ eventDragState: dragState, eventContextMenu: null }),

  updateEventDrag: (updates) => set((state) => ({
    eventDragState: state.eventDragState ? { ...state.eventDragState, ...updates } : null,
  })),

  endEventDrag: () => set({ eventDragState: null, dropTarget: null }),

  setDropTarget: (target) => set({ dropTarget: target }),

  clearDropTarget: () => set({ dropTarget: null }),

  // Sidebar drag actions
  setDraggedMaintenance: (maintenance) => set({ draggedMaintenance: maintenance }),
}));

export default useCalendarState;
